"use client";

import { useState } from "react";
import { FollowResultsCard } from "@/components/peeqd/PeeqdPreview";
import ListAvatar from "@/components/peeqd/tracking/ListAvatar";
import { proxiedInstagramImageUrl } from "@/libs/instagramImageProxy";
import { FOLLOW_LIST_DISPLAY_LIMIT } from "@/libs/tracking/constants";
import {
  INSTAGRAM_LINK_PROPS,
  instagramPostUrl,
  instagramProfileUrl,
} from "@/libs/instagramUrl";
import type {
  LikesLeaderboardPost,
  LikesLeaderboardRow,
} from "@/libs/tracking/loadLikesLeaderboard";

interface RealLikesCardProps {
  rows: LikesLeaderboardRow[];
  totalLikes: number;
  loading?: boolean;
}

const POST_GRADIENTS = [
  "from-rose/80 via-violet/70 to-rose-deep/80",
  "from-violet/70 via-rose/60 to-amber-400/70",
  "from-rose-deep/70 via-rose/60 to-violet/80",
];

function RankCircle({ rank }: { rank: number }) {
  const highlighted = rank <= 3;
  return (
    <span
      className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
        highlighted ? "grad-bg text-white" : "bg-line text-muted"
      }`}
    >
      {rank}
    </span>
  );
}

function PostThumbLink({
  post,
  gradientIndex,
}: {
  post: LikesLeaderboardPost;
  gradientIndex: number;
}) {
  const [failed, setFailed] = useState(false);
  const href = instagramPostUrl(post.permalink);
  const thumbSrc = proxiedInstagramImageUrl(post.thumbnailUrl);

  return (
    <a
      href={href}
      {...INSTAGRAM_LINK_PROPS}
      title="Open post on Instagram"
      className="shrink-0 transition hover:opacity-80"
      onClick={(e) => e.stopPropagation()}
    >
      {thumbSrc && !failed ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={thumbSrc}
          alt=""
          className="h-9 w-9 rounded object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span
          className={`grid h-9 w-9 place-items-center rounded bg-gradient-to-br ${
            POST_GRADIENTS[gradientIndex % POST_GRADIENTS.length]
          }`}
        >
          <svg
            className="h-4 w-4 text-white/90"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden
          >
            <rect x="2" y="2" width="20" height="20" rx="5" />
            <circle cx="12" cy="12" r="4" />
          </svg>
        </span>
      )}
    </a>
  );
}

function LikesListRow({ row, index }: { row: LikesLeaderboardRow; index: number }) {
  const handle = row.username.startsWith("@") ? row.username : `@${row.username}`;
  const postThumbs =
    row.posts.length > 0 ? (
      <div className="no-bar flex gap-1 overflow-x-auto sm:max-w-[14rem]">
        {row.posts.map((post, i) => (
          <PostThumbLink key={post.igPostId} post={post} gradientIndex={i} />
        ))}
      </div>
    ) : null;

  return (
    <div className="py-2.5">
      <div className="flex items-center gap-2.5 sm:gap-3">
        <RankCircle rank={index + 1} />
        <a
          href={instagramProfileUrl(row.username)}
          {...INSTAGRAM_LINK_PROPS}
          className="flex min-w-0 flex-1 items-center gap-2.5 transition hover:opacity-80 sm:gap-3"
        >
          <ListAvatar avatarUrl={row.avatarUrl} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold sm:text-[14px]">{handle}</p>
            <p className="truncate text-[11px] text-muted sm:text-[13px]">{row.displayName || handle}</p>
          </div>
        </a>
        <span className="flex shrink-0 items-center gap-1 text-[12px] font-semibold text-ink sm:text-[13px]">
          <svg
            className="h-3.5 w-3.5 text-amber-400"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          {row.likes}
        </span>
        <div className="hidden sm:block">{postThumbs}</div>
      </div>
      {postThumbs ? <div className="mt-2 pl-9 sm:hidden">{postThumbs}</div> : null}
    </div>
  );
}

function LikesLoadingShell() {
  return (
    <FollowResultsCard>
      <div className="space-y-2 py-2">
        <div className="sk h-16 rounded-xl" />
        <div className="sk h-16 rounded-xl" />
        <div className="sk h-16 rounded-xl" />
      </div>
    </FollowResultsCard>
  );
}

export default function RealLikesCard({
  rows,
  totalLikes: _totalLikes,
  loading = false,
}: RealLikesCardProps) {
  if (loading) return <LikesLoadingShell />;

  const visibleRows = rows.slice(0, FOLLOW_LIST_DISPLAY_LIMIT);

  return (
    <FollowResultsCard
      header={
        visibleRows.length > 0 ? (
          <p className="text-center text-[11.5px] font-medium text-muted">
            Showing top {visibleRows.length} by likes received - tap a post to open on Instagram
          </p>
        ) : undefined
      }
    >
      {visibleRows.length === 0 ? (
        <div className="py-6 text-center text-[13px] text-muted">
          No likes collected for this period yet.
        </div>
      ) : (
        <div className="divide-y divide-line">
          {visibleRows.map((row, i) => (
            <LikesListRow key={row.igUserId} row={row} index={i} />
          ))}
        </div>
      )}
    </FollowResultsCard>
  );
}
